import Icon from './icons.jsx'
import { useSyncStatus } from '../hooks/useSyncStatus.js'

// Banner de status da coleta inicial pós-onboarding.
//
// Estados:
//   - active (pending/running) → banner informativo "em andamento".
//   - justCompleted            → banner de sucesso (dispensável); dispara
//                                `onComplete` no hook para o dashboard refazer
//                                o fetch dos dados.
//   - falha sem nada ativo     → banner de alerta com o erro.
//   - nada relevante           → não renderiza.
//
// O polling vive no hook useSyncStatus; este componente é só apresentação.
export default function SyncStatusBanner({ onComplete }) {
  const { jobs, active, justCompleted, loading, dismissCompleted } =
    useSyncStatus({ intervalMs: 5000, onComplete })

  if (loading) return null

  const running = jobs.filter((j) => j.status === 'running').length
  const pending = jobs.filter((j) => j.status === 'pending').length
  // Falha só importa quando NÃO há nada ativo (senão ainda pode resolver).
  const failed = !active && jobs.some((j) => j.status === 'failed')

  // ----- Em andamento -----------------------------------------------------
  if (active) {
    const total = running + pending
    return (
      <Banner tone="info" icon="refresh" spin>
        <strong>Coleta inicial em andamento.</strong>{' '}
        Estamos preparando seus dados do Google Ads dos últimos 7 dias
        {total > 1 ? ` (${total} contas)` : ''}. Pode continuar navegando —
        avisamos assim que terminar.
      </Banner>
    )
  }

  // ----- Concluída agora --------------------------------------------------
  if (justCompleted) {
    return (
      <Banner tone="success" icon="check" onClose={dismissCompleted}>
        <strong>Coleta concluída!</strong> Seus dados já estão disponíveis no
        dashboard.
      </Banner>
    )
  }

  // ----- Falhou -----------------------------------------------------------
  if (failed) {
    const err = jobs.find((j) => j.status === 'failed')?.error_message
    return (
      <Banner tone="error" icon="bell">
        <strong>A coleta inicial falhou.</strong> Tentaremos novamente
        automaticamente. Se persistir, verifique a conexão do Google Ads no
        Perfil.
        {err ? <span style={{ opacity: 0.7 }}> ({err})</span> : null}
      </Banner>
    )
  }

  return null
}

// Banner genérico, alinhado ao visual de `dash-filter-banner`. Cores por tom
// seguem a paleta usada no resto do app (verde sucesso, âmbar alerta etc.).
const TONES = {
  info: { bg: '#eef4ff', border: '#c7dbff', fg: '#1f4ed8' },
  success: { bg: '#ecfdf3', border: '#bbf7d0', fg: '#15803d' },
  error: { bg: '#fef2f2', border: '#fecaca', fg: '#b91c1c' },
}

function Banner({ tone = 'info', icon, spin = false, onClose, children }) {
  const c = TONES[tone] || TONES.info
  return (
    <div
      className="sync-banner"
      role="status"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 14px',
        marginBottom: 16,
        borderRadius: 10,
        background: c.bg,
        border: `1px solid ${c.border}`,
        color: c.fg,
        fontSize: 14,
      }}
    >
      <span
        style={{
          display: 'inline-flex',
          animation: spin ? 'deebs-spin 1s linear infinite' : 'none',
        }}
      >
        <Icon n={icon} s={16} />
      </span>
      <span style={{ flex: 1 }}>{children}</span>
      {onClose ? (
        <button
          type="button"
          onClick={onClose}
          aria-label="Fechar"
          style={{
            background: 'transparent',
            border: 'none',
            color: c.fg,
            cursor: 'pointer',
            display: 'inline-flex',
          }}
        >
          <Icon n="x" s={14} />
        </button>
      ) : null}
      {/* keyframes inline para o spin sem depender do globals.css */}
      <style>{'@keyframes deebs-spin{to{transform:rotate(360deg)}}'}</style>
    </div>
  )
}
